import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

export default function DoNotSellPage() {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    state: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <>
      <Helmet><title>Do Not Sell My Info | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Do Not Sell My Personal Information</h1>
            <p className="mt-4 text-gray-300">Your California Privacy Rights</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto">
            
            {/* CCPA Overview */}
            <section className="mb-12">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-semibold text-navy mb-4">Your Rights Under CCPA</h2>
                <p className="text-gray-600 mb-4">
                  The California Consumer Privacy Act (CCPA) gives California residents the right to opt out of the "sale" of their personal information. While BESTIX does not sell personal information in the traditional sense, some data sharing with advertising partners may be considered a "sale" under CCPA's broad definition.
                </p>
                <p className="text-gray-600">
                  You can exercise your right to opt out below.
                </p>
              </div>
            </section>

            {/* What This Means */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">What Does "Sale" Mean?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-navy mb-2">We DO NOT</h3>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Sell your name, email, or contact info to third parties</li>
                    <li>• Share your payment information with marketers</li>
                    <li>• Provide your data to data brokers</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-navy mb-2">May Be Considered "Sale"</h3>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Sharing browsing data with ad networks</li>
                    <li>• Using cookies for targeted advertising</li>
                    <li>• Analytics data shared with partners</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Opt-Out Form */}
            <section className="mb-12">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-semibold text-navy mb-6">Submit Your Opt-Out Request</h2>
                
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Request Received</h3>
                    <p className="text-gray-600">
                      We'll process your request within 15 business days and send confirmation to your email.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                        State of Residence
                      </label>
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="">Select your state</option>
                        <option value="CA">California</option>
                        <option value="CO">Colorado</option>
                        <option value="CT">Connecticut</option>
                        <option value="VA">Virginia</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <button type="submit" className="w-full btn btn-primary py-4">
                      Submit Opt-Out Request
                    </button>
                  </form>
                )}
              </div>
            </section>

            {/* Additional Rights */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Your Other Privacy Rights</h2>
              <div className="bg-white rounded-xl shadow-md p-6">
                <p className="text-gray-600 mb-4">
                  California residents also have the right to:
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Know</strong> what personal information we collect and how it's used</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Delete</strong> personal information we have collected</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Non-discrimination</strong> for exercising your privacy rights</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  To exercise these rights, contact us at <span className="text-navy font-semibold">privacy@bestix.ai</span>
                </p>
              </div>
            </section>

            {/* Contact */}
            <section>
              <div className="bg-gray-100 rounded-xl p-6 text-center">
                <p className="text-gray-600">
                  Questions about your privacy rights? Contact our Privacy Team:
                </p>
                <p className="text-navy font-semibold mt-2">privacy@bestix.ai</p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </>
  );
}
